import { FC, useState, useEffect, FormEvent } from "react";
import {
  LdIcon,
  LdInput,
  LdLabel,
  LdOption,
  LdSelect,
  LdCheckbox,
  LdToggle,
} from "@emdgroup-liquid/liquid/dist/react";
import { Question, QuestionType } from "typings/app/types";
import { AnswerOptions } from "typings/api/types";
import { IconFour, IconFive } from "components/Icons";

import "./style.scss";

interface Props {
  onChange: (arg0: Question) => void;
  disabled?: boolean;
}

const DEFAULT_LIKERT_SCALE = {
  1: "strongly disagree",
  2: "disagree",
  3: "agree",
  4: "strongly agree",
};

const QuestionForm: FC<Props> = ({ disabled = false, onChange }) => {
  const [questionType, setQuestionType] = useState<QuestionType>(
    QuestionType.FREE_TEXT
  );
  const [questionText, setQuestionText] = useState<string | undefined>();
  const [answerOptions, setAnswerOptions] = useState<AnswerOptions[]>();
  const [mandatory, setMandatory] = useState<boolean>(false);
  const [allowNeutralLikert, setAllowNeutralLikert] = useState<boolean>(false);
  const [likertScale, setLikertScale] =
    useState<{ [key: string]: string }>(DEFAULT_LIKERT_SCALE);
  const [selectOptions, setSelectOptions] = useState<string[]>([]);
  const [optionText, setOptionText] = useState<string | undefined>();

  const handleLikertScaleChange = (key: number, label: string) => {
    setLikertScale((state) => ({ ...state, [key]: label }));
  };

  const handleAddOption = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (optionText !== undefined)
      setSelectOptions((state) => [...state, optionText]);
    setOptionText(undefined);
  };

  useEffect(() => {
    const question = {
      questionType,
      questionText,
      options: answerOptions,
      mandatory,
    } as Question;
    onChange(question);
  }, [answerOptions, questionText, mandatory]);

  useEffect(() => {
    if (allowNeutralLikert) {
      if (likertScale[5] === undefined) {
        setLikertScale(({ 3: three, 4: four, ...state }) => ({
          ...state,
          3: "",
          4: three,
          5: four,
        }));
      }
    } else {
      if (likertScale[5] !== undefined) {
        setLikertScale(({ 3: three, 4: four, 5: five, ...state }) => ({
          ...state,
          3: four,
          4: five,
        }));
      }
    }
  }, [allowNeutralLikert, likertScale]);

  useEffect(() => {
    switch (questionType) {
      case QuestionType.SELECT:
      case QuestionType.MULTI_SELECT:
        setAnswerOptions(
          selectOptions.map((option) => ({
            label: option,
            value: option,
          }))
        );
        break;
      case QuestionType.LIKERT_SCALE:
        setAnswerOptions(
          Object.keys(likertScale).map((key) => ({
            value: parseInt(key),
            label: likertScale[key],
          }))
        );
        break;
      default:
        setAnswerOptions([]);
    }
  }, [questionType, selectOptions, likertScale]);

  const renderOptions = () => {
    switch (questionType) {
      case QuestionType.LIKERT_SCALE:
        return (
          <ul className="flex flex-col gap-ld-12 w-full">
            <LdLabel position="left" className="flex items-start w-max">
              <span className="w-max">Options</span>
              <LdToggle
                disabled={disabled}
                size="sm"
                checked={allowNeutralLikert}
                onInput={(ev) =>
                  setAllowNeutralLikert(
                    (ev.target as HTMLLdCheckboxElement).checked
                  )
                }
              >
                <LdIcon size="sm" slot="icon-start">
                  <IconFour />
                </LdIcon>
                <LdIcon size="sm" slot="icon-end">
                  <IconFive />
                </LdIcon>
              </LdToggle>
            </LdLabel>
            <LdLabel>Scale</LdLabel>
            {Object.keys(likertScale).map((key) => (
              <li
                key={`${key}`}
                className="flex flex-row gap-ld-24 items-center w-full"
              >
                <LdLabel>{key}</LdLabel>
                <LdInput
                  disabled={disabled}
                  className="flex-grow"
                  onInput={(ev) =>
                    handleLikertScaleChange(
                      parseInt(key),
                      `${(ev.target as HTMLLdInputElement).value}`
                    )
                  }
                  value={likertScale[key].toString()}
                />
              </li>
            ))}
          </ul>
        );
      case QuestionType.SELECT:
      case QuestionType.MULTI_SELECT:
        return (
          <form onSubmit={handleAddOption} className="flex flex-col gap-ld-32">
            <LdLabel>
              Options
              {selectOptions.length > 0 && (
                <LdSelect
                  name="options"
                  onClick={(e) => e.preventDefault()}
                  className="question-options"
                  popperClass="question-options-popper"
                  multiple
                  onInput={
                    ((
                      event: React.SyntheticEvent<
                        HTMLInputElement,
                        CustomEvent<string[]>
                      >
                    ) => {
                      setSelectOptions(event.nativeEvent.detail);
                    }) as unknown as any
                  }
                >
                  {selectOptions.map((option) => (
                    <LdOption key={option} value={option} selected>
                      {option}
                    </LdOption>
                  ))}
                  <LdIcon name="" />
                </LdSelect>
              )}
            </LdLabel>
            <LdInput
              disabled={disabled}
              value={optionText}
              onInput={(ev) => {
                setOptionText((ev.target as HTMLLdInputElement).value);
              }}
            />
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col my-ld-16">
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-ld-24 mb-ld-32">
        <LdLabel>
          Question Text
          <LdInput
            disabled={disabled}
            placeholder="New Survey"
            tone="dark"
            value={questionText}
            onInput={(ev) => {
              setQuestionText((ev.target as HTMLLdInputElement).value);
            }}
            onBlur={(ev) => {
              setQuestionText(ev.target.value);
            }}
          />
        </LdLabel>
        <LdLabel>
          <span className="flex justify-between">Type</span>
          <LdSelect
            disabled={disabled}
            onInput={
              ((
                event: React.SyntheticEvent<
                  HTMLInputElement,
                  CustomEvent<QuestionType[]>
                >
              ) => {
                setQuestionType(event.nativeEvent.detail[0]);
              }) as unknown as any
            }
            placeholder="Select"
          >
            {Object.keys(QuestionType).map((key) => {
              const val = QuestionType[key as keyof typeof QuestionType];
              return (
                <LdOption key={key} value={val} selected={questionType === val}>
                  {val}
                </LdOption>
              );
            })}
          </LdSelect>
        </LdLabel>
        <LdLabel position="right" size="m">
          Is Mandatory
          <LdCheckbox
            disabled={disabled}
            checked={mandatory}
            onInput={(ev) =>
              setMandatory((ev.target as HTMLLdCheckboxElement).checked)
            }
          />
        </LdLabel>
      </div>
      <div>{renderOptions()}</div>
    </div>
  );
};

export default QuestionForm;
